import {
  limit,
  orderBy,
  startAfter,
  Timestamp,
  where,
  type QueryConstraint,
  type QueryDocumentSnapshot,
  type DocumentData,
} from 'firebase/firestore'
import { callable } from './callable'
import type { TelemetryLogEntry } from '../types/telemetry'

export type LogRow = TelemetryLogEntry & {
  id: string
}

export type Unsubscribe = () => void

export interface TelemetryLogCursor {
  id: string
  _docSnap?: QueryDocumentSnapshot<DocumentData>
}

export interface TelemetryLogFilters {
  debugSessionId?: string
  range?: '1h' | '24h' | '7d' | '30d' | 'all' | string
  rangeMs?: number | null
  uid?: string
  workspaceId?: string
  limit?: number
}

export interface ListTelemetryLogsResult {
  logs: LogRow[]
  nextCursor: TelemetryLogCursor | null
  hasMore: boolean
}

/**
 * Builds Firestore query constraints from filters and optional cursor.
 */
export function buildQueryConstraints(
  filters: TelemetryLogFilters = {},
  cursor?: TelemetryLogCursor | null,
): QueryConstraint[] {
  const constraints: QueryConstraint[] = []
  const limitCount = filters.limit ?? 100

  // In trace mode: order by ts ASC and filter by debugSessionId
  if (filters.debugSessionId) {
    constraints.push(where('debugSessionId', '==', filters.debugSessionId.trim()))
    constraints.push(orderBy('ts', 'asc'))
    constraints.push(limit(limitCount))
  } else {
    // Date range calculation
    let rangeMs: number | null = 24 * 60 * 60 * 1000
    if (filters.rangeMs !== undefined) {
      rangeMs = filters.rangeMs
    } else if (filters.range === '1h') {
      rangeMs = 60 * 60 * 1000
    } else if (filters.range === '24h') {
      rangeMs = 24 * 60 * 60 * 1000
    } else if (filters.range === '7d') {
      rangeMs = 7 * 24 * 60 * 60 * 1000
    } else if (filters.range === '30d') {
      rangeMs = 30 * 24 * 60 * 60 * 1000
    } else if (filters.range === 'all') {
      rangeMs = null
    }

    if (rangeMs !== null) {
      const minTs = Timestamp.fromMillis(Date.now() - rangeMs)
      constraints.push(where('ts', '>=', minTs))
    }

    if (filters.uid && filters.uid.trim()) {
      constraints.push(where('uid', '==', filters.uid.trim()))
    }
    if (filters.workspaceId && filters.workspaceId.trim()) {
      constraints.push(where('workspaceId', '==', filters.workspaceId.trim()))
    }

    constraints.push(orderBy('ts', 'desc'))
    constraints.push(limit(limitCount))
  }

  if (cursor) {
    const snap = cursor._docSnap || (cursor as unknown as QueryDocumentSnapshot<DocumentData>)
    if (snap) {
      constraints.push(startAfter(snap))
    }
  }

  return constraints
}

/**
 * Fetches paginated telemetry logs matching filters via server callable.
 */
export async function listTelemetryLogs(
  filters: TelemetryLogFilters = {},
  cursor?: TelemetryLogCursor | null,
  accessReason?: { code: string; linkedId?: string },
): Promise<ListTelemetryLogsResult> {
  return callable<{ filters: TelemetryLogFilters; cursor?: { id: string } | null; accessReason?: unknown }, ListTelemetryLogsResult>(
    'listOwnerTelemetryLogs',
    {
      filters,
      cursor: cursor ? { id: cursor.id } : null,
      accessReason,
    },
  )
}

/**
 * Retrieves a single telemetry log document by ID via server callable.
 */
export async function getTelemetryLogById(
  id: string,
  accessReason?: { code: string; linkedId?: string },
): Promise<LogRow | null> {
  if (!id || !id.trim()) return null
  return callable<{ id: string; accessReason?: unknown }, LogRow | null>(
    'getOwnerTelemetryLog',
    {
      id: id.trim(),
      accessReason,
    },
  )
}

/**
 * Subscribes to telemetry logs stream matching filters (polled list every 15 s while mounted).
 */
export function subscribeToTelemetryLogs(
  filters: TelemetryLogFilters = {},
  callback: (logs: LogRow[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe {
  let mounted = true
  const fetchLogs = async () => {
    try {
      const res = await listTelemetryLogs(filters)
      if (mounted) {
        callback(res.logs)
      }
    } catch (err) {
      if (mounted) {
        if (onError) {
          onError(err as Error)
        } else {
          console.warn('[telemetryLogsService] subscribeToTelemetryLogs error:', err)
        }
      }
    }
  }

  void fetchLogs()
  const intervalId = setInterval(fetchLogs, 15_000)

  return () => {
    mounted = false
    clearInterval(intervalId)
  }
}
